/* eslint-env browser */
// Transaction logs (wave 3, W2 look): the finance hub. Tabs: Dashboard
// (default), Transaction logs, Flat rate and Finance & admin controls
// (admin / finance), How it's calculated.
//
// Data, exactly as before (no new reads):
// - The wallet history of every driver (fetchAllWalletHistory, ~4-20 MB) is
//   fetched only while the Logs tab or the Finance & admin tab is open, and
//   dropped again (clearAllWalletHistory) when that tab closes. The effect
//   below is the old one, unchanged.
// - The four totals above the table come from /transactionStats for the
//   chosen days (fetchStatsCached, the 5-minute cache the Dashboard shares);
//   with a search or a source chosen they are summed from the rows on screen.
// - Days are Addis Ababa days (the /transactionStats keys are Addis dates).
// Filters are chips (period, source), a search box and two Addis date
// fields; the table exports every filtered row (CSV and print).
// Call-centre feedback (29 Oct 2026): the period and the dates fold away
// with "Hide filters" (kept per person in this browser; the active filters
// stay as chips above the table), each date clears with its own X, and a
// copy button beside every phone number copies it without spaces.
import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Paper from '@mui/material/Paper';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import TrendingDownRoundedIcon from '@mui/icons-material/TrendingDownRounded';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import { api } from 'common';
import DashboardCard from '../components/DashboardCard';
import ClickableUserLink from '../components/ClickableUserLink';
import { tokens as c } from '../theme';
import { formatAddisDate, addisDateStamp } from '../common/exportUtils';
import {
  DataTable,
  SearchField,
  ChipGroup,
  DropChip,
  ActiveFilters,
  CopyButton,
  FilterToggleButton,
  useFiltersOpen,
  formatMoney,
  formatNumber,
  phoneMatches,
} from '../components/modern';
import DateFilterField from '../components/modern/DateFilterField';
import FinanceAdminControls from './FinanceAdminControls';
import FinanceDashboard from './finance/FinanceDashboard';
import MetricsGlossary from './finance/MetricsGlossary';
import FlatRateMetrics from './finance/FlatRateMetrics';
import { SOURCE_BUCKETS, getTransactionSource, fetchStatsCached } from './finance/sourceBuckets';

const DAY_MS = 24 * 60 * 60 * 1000;

// 'YYYY-MM-DD' (an Addis Ababa date) -> the instant that Addis day starts.
const keyToMs = (key) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(key || ''));
  if (!m) return null;
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) - 3 * 60 * 60 * 1000;
};
// An instant -> its Addis Ababa date 'YYYY-MM-DD'.
const msToKey = (ms) => (ms === null || ms === undefined || ms === '' ? '' : addisDateStamp(Number(ms)));
// The Addis date `days` days before today.
const daysAgoKey = (days) => addisDateStamp(Date.now() - days * DAY_MS);
// The Addis date one calendar year before today (29 Feb -> 28 Feb).
const yearAgoKey = () => {
  const today = addisDateStamp(Date.now());
  const [y, m, d] = today.split('-').map(Number);
  const last = new Date(Date.UTC(y - 1, m, 0)).getUTCDate();
  const pad = (n) => (n < 10 ? `0${n}` : String(n));
  return `${y - 1}-${pad(m)}-${pad(Math.min(d, last))}`;
};

export default function TransactionLogs() {
  const { t, i18n } = useTranslation();
  const settings = useSelector((state) => state.settingsdata.settings);
  const usersdata = useSelector((state) => state.usersdata);
  const auth = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const { fetchAllWalletHistory, clearAllWalletHistory } = api;
  // A source as words: its translation, else the bucket's English label.
  const sourceLabel = (key) => {
    const bucket = SOURCE_BUCKETS[key];
    if (!bucket) return key || '';
    return t(`tx_source_${key}`, { defaultValue: bucket.label });
  };

  // Tab 0 = Dashboard (default landing), Tab 1 = Transaction Logs, Tab 2 = Flat rate,
  // Tab 3 = Finance Admin (both admin / finance only), last = Glossary.
  const [activeTab, setActiveTab] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [startDate, setStartDate] = useState(''); // Addis date 'YYYY-MM-DD' or ''
  const [endDate, setEndDate] = useState('');
  const [dateFilterType, setDateFilterType] = useState('all'); // all, today, week, month, year, custom
  const [sourceFilter, setSourceFilter] = useState('all'); // 'all' or one of SOURCE_BUCKETS keys
  const [page, setPage] = useState(0);
  // The period chips and the two dates fold away (this person's choice, kept
  // in this browser); the search and the source stay in sight.
  const [filtersOpen, toggleFilters] = useFiltersOpen('transaction_logs');
  // Pre-aggregated stats for the current date range (filled by an
  // async fetch on filter change). Drives the totals so they work even
  // before per-driver wallet history rows arrive, and cost O(days) reads
  // instead of O(transactions).
  const [rangeStats, setRangeStats] = useState({ total: 0, byType: {}, bySource: {}, days: 0, loading: false });
  // Whether the walletHistory fetch of THIS mount has completed: even if a
  // stale `{}` sits in Redux from a previous mount, the table stays in its
  // loading state until this mount's fetch resolves.
  const [walletHistoryFetched, setWalletHistoryFetched] = useState(false);

  const isAdminOrFinance = auth.profile?.usertype === 'admin' || auth.profile?.usertype === 'finance';
  const allowPrivate = !!(settings && settings.AllowCriticalEditsAdmin);

  // Defer the walletHistory fetch until the user actually opens a tab that
  // reads it (Logs OR Finance Admin Controls). Dashboard and Glossary do not
  // need walletHistory, so skipping the ~4-20 MB fetch when those are the
  // only tabs viewed is a free cost win. FinanceAdminControls reads from the
  // same Redux slice so the parent fetches for both Logs (1) and Finance
  // Admin (3).
  useEffect(() => {
    // Logs (1) and Finance Admin (3 when shown) read walletHistory;
    // the Flat Rate tab (2) reads only its own plan nodes.
    const needsWalletHistory = activeTab === 1 || (isAdminOrFinance && activeTab === 3);
    if (!needsWalletHistory) return undefined;
    setWalletHistoryFetched(false); // reset so the loader shows during the refresh
    let cancelled = false;
    Promise.resolve(dispatch(fetchAllWalletHistory()))
      .finally(() => { if (!cancelled) setWalletHistoryFetched(true); });
    return () => {
      cancelled = true;
      dispatch(clearAllWalletHistory());
    };
    // The role does not change while the page is open (as before).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, dispatch, fetchAllWalletHistory, clearAllWalletHistory]);

  // Fetch aggregated stats for the active date range. Routed through the
  // same 5-min TTL cache the Dashboard uses, so switching between tabs with
  // the same date filter is instant.
  useEffect(() => {
    let cancelled = false;
    setRangeStats((prev) => ({ ...prev, loading: true }));
    fetchStatsCached(dispatch, startDate || null, endDate || null)
      .then((result) => {
        if (cancelled) return;
        setRangeStats({ ...result, loading: false });
      })
      .catch(() => {
        if (cancelled) return;
        setRangeStats({ total: 0, byType: {}, bySource: {}, days: 0, loading: false });
      });
    return () => { cancelled = true; };
  }, [dispatch, startDate, endDate]);

  // Wallet history rows, one per transaction (as before).
  const data = useMemo(() => {
    if (!usersdata.users || !auth.allWalletHistory) return [];

    const driverMap = {};
    usersdata.users.forEach((user) => {
      if (user.usertype === 'driver') {
        driverMap[user.id] = {
          name: `${user.firstName} ${user.lastName}`,
          mobile: user.mobile,
          email: user.email,
          walletBalance: user.walletBalance || 0,
        };
      }
    });

    const transactions = [];
    const seenTransactionIds = new Set();
    const walletHistoryData = auth.allWalletHistory;

    Object.keys(walletHistoryData).forEach((driverId) => {
      const driverInfo = driverMap[driverId];
      if (driverInfo) {
        const driverTransactions = walletHistoryData[driverId];
        if (driverTransactions && typeof driverTransactions === 'object') {
          Object.keys(driverTransactions).forEach((txId) => {
            const transaction = driverTransactions[txId];
            if (!transaction || typeof transaction !== 'object') return;

            const actualTransactionId = transaction.transaction_id || transaction.txRef || '';
            const uniqueKey = actualTransactionId ? `${actualTransactionId}_${transaction.type}` : `${driverId}_${txId}`;
            if (seenTransactionIds.has(uniqueKey)) return;
            seenTransactionIds.add(uniqueKey);

            const rowForSource = {
              source: transaction.source || null,
              txRef: transaction.txRef || '',
              operatorUid: transaction.operatorUid || null,
              type: transaction.type || '',
              remarks: transaction.remarks || transaction.remark || '',
            };
            const sourceBucket = getTransactionSource(rowForSource);

            transactions.push({
              id: `${driverId}_${txId}`,
              driverId: driverId,
              driverName: driverInfo.name,
              driverMobile: driverInfo.mobile || '',
              driverEmail: driverInfo.email || '',
              amount: transaction.amount || 0,
              date: transaction.date,
              transaction_id: actualTransactionId,
              type: transaction.type || '',
              remarks: transaction.remarks || transaction.remark || '',
              source: transaction.source || null,
              txRef: transaction.txRef || '',
              operatorUid: transaction.operatorUid || null,
              sourceLabel: sourceBucket.label,
              sourceKey: sourceBucket.key,
              walletBalance: driverInfo.walletBalance,
            });
          });
        }
      }
    });

    transactions.sort((a, b) => (b.date || 0) - (a.date || 0));
    return transactions;
  }, [usersdata.users, auth.allWalletHistory]);

  // Period chips (Addis days).
  const handleQuickDateFilter = (filterType) => {
    setDateFilterType(filterType);
    setPage(0);
    const today = daysAgoKey(0);
    switch (filterType) {
      case 'today':
        setStartDate(today);
        setEndDate(today);
        break;
      case 'week':
        setStartDate(daysAgoKey(7));
        setEndDate(today);
        break;
      case 'month':
        setStartDate(daysAgoKey(30));
        setEndDate(today);
        break;
      case 'year':
        setStartDate(yearAgoKey());
        setEndDate(today);
        break;
      case 'all':
        setStartDate('');
        setEndDate('');
        break;
      default:
        break;
    }
  };

  const handleClearFilters = () => {
    setSearchText('');
    setStartDate('');
    setEndDate('');
    setDateFilterType('all');
    setSourceFilter('all');
    setPage(0);
  };

  // Search (only what may be seen: phone and email stay out in demo mode)
  // and the chosen days, before the source chip.
  const searchedDated = useMemo(() => {
    let filtered = data;
    const q = searchText.trim();
    if (q) {
      const searchLower = q.toLowerCase();
      filtered = filtered.filter((transaction) =>
        (transaction.driverName && transaction.driverName.toLowerCase().includes(searchLower)) ||
        (allowPrivate && transaction.driverMobile && (transaction.driverMobile.toLowerCase().includes(searchLower) || phoneMatches(transaction.driverMobile, q))) ||
        (allowPrivate && transaction.driverEmail && transaction.driverEmail.toLowerCase().includes(searchLower)) ||
        (transaction.transaction_id && String(transaction.transaction_id).toLowerCase().includes(searchLower))
      );
    }
    if (startDate || endDate) {
      const start = startDate ? keyToMs(startDate) : null;
      const end = endDate ? keyToMs(endDate) + DAY_MS - 1 : null;
      filtered = filtered.filter((transaction) => {
        if (!transaction.date) return false;
        const ts = typeof transaction.date === 'number' ? transaction.date : new Date(transaction.date).getTime();
        if (start && end) return ts >= start && ts <= end;
        if (start) return ts >= start;
        if (end) return ts <= end;
        return true;
      });
    }
    return filtered;
  }, [data, searchText, startDate, endDate, allowPrivate]);

  const filteredData = useMemo(
    () => (sourceFilter && sourceFilter !== 'all' ? searchedDated.filter((row) => row.sourceKey === sourceFilter) : searchedDated),
    [searchedDated, sourceFilter]
  );

  const sourceCounts = useMemo(() => {
    const out = {};
    searchedDated.forEach((row) => {
      out[row.sourceKey] = (out[row.sourceKey] || 0) + 1;
    });
    return out;
  }, [searchedDated]);

  // Totals: with a search or a source chosen, summed from the rows on screen
  // (they then match the table); otherwise /transactionStats for the days
  // (complete, not bounded by the per-driver wallet history fetch).
  const searchActive = searchText && searchText.trim().length > 0;
  const sourceActive = sourceFilter && sourceFilter !== 'all';
  const filterActive = searchActive || sourceActive;
  const summaryStats = useMemo(() => {
    if (!filterActive) return null; // fall through to rangeStats
    let credit = 0;
    let debit = 0;
    filteredData.forEach((row) => {
      const amt = parseFloat(row.amount) || 0;
      if (row.type === 'Credit') credit += amt;
      else if (row.type === 'Debit') debit += amt;
    });
    return { credit, debit };
  }, [filterActive, filteredData]);

  let credit;
  let debit;
  if (summaryStats) {
    credit = summaryStats.credit;
    debit = summaryStats.debit;
  } else {
    credit = parseFloat(rangeStats.byType.Credit) || 0;
    debit = parseFloat(rangeStats.byType.Debit) || 0;
  }
  const net = credit - debit;
  const volume = credit + debit;
  const statsLoading = rangeStats.loading && !summaryStats;
  const money = (v) => formatMoney(parseFloat(v) || 0, settings, true);

  const hiddenText = t('hidden_demo');
  const phoneWord = t('phone', { defaultValue: 'Phone number' });
  const typeWord = (type) =>
    type === 'Credit'
      ? t('credit', { defaultValue: 'Credit' })
      : type === 'Debit'
        ? t('tx_type_debit', { defaultValue: 'Debit' })
        : type === 'Processing'
          ? t('tx_type_processing', { defaultValue: 'Processing' })
          : type || t('unknown', { defaultValue: 'Unknown' });

  const columns = [
    { title: t('date'), field: 'date', type: 'datetime', defaultSort: 'desc' },
    {
      title: t('driver_name'),
      field: 'driverName',
      render: (rowData) =>
        rowData.driverName ? (
          <ClickableUserLink value={rowData.driverName} type="driver" searchField="name" userId={rowData.driverId} iconType="car" />
        ) : (
          <Box component="span" sx={{ color: c.TEXT_3 }}>—</Box>
        ),
    },
    {
      title: t('mobile'),
      field: 'driverMobile',
      nowrap: true,
      exportValue: (row) => (allowPrivate ? row.driverMobile : hiddenText),
      render: (rowData) =>
        !allowPrivate ? (
          <Box component="span" sx={{ color: c.TEXT_3 }}>{hiddenText}</Box>
        ) : rowData.driverMobile ? (
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
            <ClickableUserLink value={rowData.driverMobile} type="driver" searchField="phone" userId={rowData.driverId} iconType="phone" />
            <CopyButton value={rowData.driverMobile} phone what={phoneWord} sx={{ my: -0.5 }} />
          </Box>
        ) : (
          <Box component="span" sx={{ color: c.TEXT_3 }}>—</Box>
        ),
    },
    {
      title: t('email'),
      field: 'driverEmail',
      hideable: true,
      exportValue: (row) => (allowPrivate ? row.driverEmail : hiddenText),
      render: (row) => (allowPrivate ? row.driverEmail || <Box component="span" sx={{ color: c.TEXT_3 }}>—</Box> : <Box component="span" sx={{ color: c.TEXT_3 }}>{hiddenText}</Box>),
    },
    { title: t('transaction_id', { defaultValue: 'Transaction ID' }).replace(/\s*:\s*$/, ''), field: 'transaction_id', cellSx: { fontSize: 13, fontVariantNumeric: 'tabular-nums' } },
    {
      title: t('amount'),
      field: 'amount',
      type: 'money',
      render: (rowData) => {
        const amount = rowData.amount || 0;
        return settings?.swipe_symbol ? `${amount} ${settings.symbol}` : `${settings?.symbol} ${amount}`;
      },
    },
    {
      title: t('type'),
      field: 'type',
      type: 'status',
      exportValue: (row) => row.type,
      searchValue: (row) => typeWord(row.type),
      status: (rowData) =>
        rowData.type === 'Debit'
          ? { tone: 'error', icon: <ArrowUpwardRoundedIcon />, label: typeWord(rowData.type) }
          : rowData.type === 'Credit'
            ? { tone: 'success', icon: <ArrowDownwardRoundedIcon />, label: typeWord(rowData.type) }
            : { tone: 'warning', icon: <HourglassEmptyRoundedIcon />, label: typeWord(rowData.type) },
    },
    {
      // Source bucket: the server-set source when present, else matched from
      // txRef / remarks (getTransactionSource), so admins see where money
      // comes from (dispatched bookings, Donkey Meter, top-ups, admin credits...).
      title: t('source', { defaultValue: 'Source' }),
      field: 'sourceLabel',
      exportValue: (row) => sourceLabel(row.sourceKey),
      searchValue: (row) => sourceLabel(row.sourceKey),
      sortValue: (row) => sourceLabel(row.sourceKey),
      render: (rowData) => (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1, py: 0.25, borderRadius: '13px', bgcolor: c.SURFACE_ALT, color: c.INK, fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
          <Box component="span" aria-hidden="true" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: SOURCE_BUCKETS[rowData.sourceKey]?.color || c.TEXT_3, flex: 'none' }} />
          {sourceLabel(rowData.sourceKey)}
        </Box>
      ),
    },
    { title: t('remarks'), field: 'remarks', hideable: true },
  ];

  // Loading gate of the Logs table: this mount's fetch has finished, Redux
  // says it is done, and the users (for the driver names) are loaded.
  const isLoadingLogsData =
    activeTab === 1 && (!walletHistoryFetched || auth.allWalletHistoryLoading || usersdata.loading || !usersdata.users);

  const periodOptions = [
    { value: 'all', label: t('all_time', { defaultValue: 'All time' }) },
    { value: 'today', label: t('today') },
    { value: 'week', label: t('last_7_days', { defaultValue: 'Last 7 days' }) },
    { value: 'month', label: t('last_30_days', { defaultValue: 'Last 30 days' }) },
    { value: 'year', label: t('tx_period_year', { defaultValue: 'Last year' }) },
  ];
  const sourceOptions = [
    { value: 'all', label: t('all_sources', { defaultValue: 'All sources' }), count: searchedDated.length },
    ...Object.keys(SOURCE_BUCKETS).map((key) => ({ value: key, label: sourceLabel(key), count: sourceCounts[key] || 0 })),
  ];

  const rangeText = () => {
    if (startDate && endDate) return startDate === endDate ? formatAddisDate(keyToMs(startDate)) : `${formatAddisDate(keyToMs(startDate))} – ${formatAddisDate(keyToMs(endDate))}`;
    if (startDate) return t('tx_filter_from', { date: formatAddisDate(keyToMs(startDate)), defaultValue: 'From {{date}}' });
    if (endDate) return t('tx_filter_until', { date: formatAddisDate(keyToMs(endDate)), defaultValue: 'Until {{date}}' });
    return '';
  };
  const filterTokens = [];
  if (startDate || endDate) {
    const preset = periodOptions.find((o) => o.value === dateFilterType && o.value !== 'all');
    filterTokens.push({ key: 'dates', group: t('tx_period', { defaultValue: 'Period' }), value: preset ? preset.label : rangeText() });
  }
  if (sourceActive) filterTokens.push({ key: 'source', group: t('source', { defaultValue: 'Source' }), value: sourceLabel(sourceFilter) });
  if (searchActive) filterTokens.push({ key: 'search', group: t('search'), value: searchText.trim() });
  const removeToken = (key) => {
    setPage(0);
    if (key === 'dates') handleQuickDateFilter('all');
    if (key === 'source') setSourceFilter('all');
    if (key === 'search') setSearchText('');
  };

  const countText = ({ loading }) => {
    if (loading) return t('loading', { defaultValue: 'Loading…' });
    if (filterTokens.length) {
      return t('tx_results_count', { shown: formatNumber(filteredData.length), total: formatNumber(data.length), defaultValue: '{{shown}} of {{total}} transactions' });
    }
    return t('tx_count', { count: data.length, n: formatNumber(data.length), defaultValue_one: '{{n}} transaction', defaultValue_other: '{{n}} transactions', defaultValue: '{{n}} transactions' });
  };

  // The line under the totals: what they add up.
  const totalsNote = (() => {
    if (summaryStats) {
      const parts = [];
      if (searchActive) parts.push(`${t('tx_totals_search', { defaultValue: 'search' })} “${searchText.trim()}”`);
      if (sourceActive) parts.push(`${t('tx_totals_source', { defaultValue: 'source' })} ${sourceLabel(sourceFilter)}`);
      return [
        t('tx_totals_for', { defaultValue: 'Showing totals for' }),
        parts.join(' + '),
        t('tx_totals_summed', { n: filteredData.length, defaultValue: '— summed from the {{n}} visible row(s).' }),
        sourceActive ? t('tx_totals_capped', { defaultValue: 'For very active drivers older entries may be capped (200 per driver); switch to the Dashboard tab for the authoritative cross-period figure.' }) : '',
        t('tx_volume_note', { defaultValue: 'Volume = total of all credits and debits. Net = what stayed on the platform.' }),
      ].filter(Boolean).join(' ');
    }
    if (rangeStats.days > 0 && !statsLoading) {
      return [
        startDate || endDate
          ? t('tx_aggregated_range', { n: rangeStats.days, defaultValue: 'Aggregated over {{n}} day(s) in the selected range.' })
          : t('tx_aggregated_all', { n: rangeStats.days, defaultValue: 'Aggregated over {{n}} day(s) (all time).' }),
        t('tx_volume_note', { defaultValue: 'Volume = total of all credits and debits. Net = what stayed on the platform.' }),
      ].join(' ');
    }
    return '';
  })();

  const tabs = [
    { key: 'dashboard', label: t('dashboard_text'), icon: <SpaceDashboardOutlinedIcon /> },
    { key: 'logs', label: t('transaction_logs'), icon: <ReceiptLongOutlinedIcon /> },
    ...(isAdminOrFinance
      ? [
          { key: 'flat', label: t('flat_rate_tab', { defaultValue: 'Flat rate' }), icon: <EventAvailableOutlinedIcon /> },
          { key: 'admin', label: t('finance_admin_controls'), icon: <AccountBalanceOutlinedIcon /> },
        ]
      : []),
    { key: 'glossary', label: t('how_its_calculated', { defaultValue: "How it's calculated" }), icon: <MenuBookOutlinedIcon /> },
  ];
  const glossaryIndex = isAdminOrFinance ? 4 : 2;
  const logsTitle = t('transaction_logs');

  return (
    <Box dir={i18n.dir()} sx={{ width: '100%' }}>
      <Tabs
        value={activeTab}
        onChange={(e, v) => setActiveTab(v)}
        aria-label={t('transaction_logs')}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ borderBottom: `1px solid ${c.OUTLINE}`, '& .MuiTab-root': { minHeight: 48 }, '& .MuiTab-iconWrapper': { fontSize: 20 } }}
      >
        {tabs.map((tab, i) => (
          <Tab key={tab.key} label={tab.label} icon={tab.icon} iconPosition="start" id={`tx-tab-${i}`} aria-controls={`tx-tabpanel-${i}`} />
        ))}
      </Tabs>

      <Box role="tabpanel" id={`tx-tabpanel-${activeTab}`} aria-labelledby={`tx-tab-${activeTab}`} sx={{ pt: 2.5 }}>
        {/* Tab 0: Finance Dashboard (default landing) */}
        {activeTab === 0 && <FinanceDashboard settings={settings} dispatch={dispatch} />}

        {/* Tab 1: Transaction Logs */}
        {activeTab === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Paper sx={{ p: { xs: 2, md: 2.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 1.5, columnGap: 2 }}>
                <SearchField
                  value={searchText}
                  onChange={(v) => {
                    setSearchText(v);
                    setPage(0);
                  }}
                  placeholder={
                    allowPrivate
                      ? t('tx_search_hint', { defaultValue: 'Search name, phone, email or transaction ID' })
                      : t('tx_search_hint_demo', { defaultValue: 'Search name or transaction ID' })
                  }
                />
                <DropChip
                  label={t('source', { defaultValue: 'Source' })}
                  value={sourceFilter}
                  allValue="all"
                  options={sourceOptions}
                  onChange={(v) => {
                    setSourceFilter(v);
                    setPage(0);
                  }}
                />
                <FilterToggleButton
                  open={filtersOpen}
                  onClick={toggleFilters}
                  activeCount={startDate || endDate ? 1 : 0}
                  controls="tx-more-filters"
                  sx={{ marginInlineStart: 'auto' }}
                />
              </Box>
              <Collapse in={filtersOpen} id="tx-more-filters">
                <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <ChipGroup
                    label={t('tx_period', { defaultValue: 'Period' })}
                    value={dateFilterType}
                    options={periodOptions}
                    onChange={handleQuickDateFilter}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', rowGap: 2, columnGap: 2 }}>
                    <DateFilterField
                      id="tx-from"
                      label={t('start_date')}
                      value={keyToMs(startDate)}
                      onChange={(ms) => {
                        setStartDate(msToKey(ms));
                        setDateFilterType('custom');
                        setPage(0);
                      }}
                      onClear={() => {
                        setStartDate('');
                        setDateFilterType(endDate ? 'custom' : 'all');
                        setPage(0);
                      }}
                    />
                    <DateFilterField
                      id="tx-to"
                      label={t('end_date')}
                      value={keyToMs(endDate)}
                      onChange={(ms) => {
                        setEndDate(msToKey(ms));
                        setDateFilterType('custom');
                        setPage(0);
                      }}
                      onClear={() => {
                        setEndDate('');
                        setDateFilterType(startDate ? 'custom' : 'all');
                        setPage(0);
                      }}
                    />
                  </Box>
                </Box>
              </Collapse>
            </Paper>

            {/* Totals for the chosen days: Inflow − Outflow = Net, and Volume. */}
            <Box>
              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' } }}>
                <DashboardCard icon={<TrendingUpRoundedIcon />} hue="success" title={t('tx_inflow', { defaultValue: 'Inflow (credits)' })} value={money(credit)} loading={statsLoading} />
                <DashboardCard icon={<TrendingDownRoundedIcon />} hue="danger" title={t('tx_outflow', { defaultValue: 'Outflow (debits)' })} value={money(debit)} loading={statsLoading} />
                <DashboardCard
                  icon={<AccountBalanceWalletOutlinedIcon />}
                  hue={net >= 0 ? 'accent' : 'danger'}
                  title={t('tx_net', { defaultValue: 'Net (inflow − outflow)' })}
                  value={<Box component="span" sx={{ color: net >= 0 ? c.INK : c.DANGER_TEXT }}>{money(net)}</Box>}
                  loading={statsLoading}
                />
                <DashboardCard icon={<ReceiptLongOutlinedIcon />} hue="neutral" title={t('tx_total_volume', { defaultValue: 'Total volume' })} value={money(volume)} loading={statsLoading} />
              </Box>
              {totalsNote ? (
                <Typography variant="body2" sx={{ mt: 1.25, color: c.TEXT_3 }}>
                  {totalsNote}
                </Typography>
              ) : null}
            </Box>

            <DataTable
              title={logsTitle}
              rows={filteredData}
              columns={columns}
              loading={isLoadingLogsData}
              countText={countText}
              headerContent={filterTokens.length ? <ActiveFilters tokens={filterTokens} onRemove={removeToken} onClearAll={handleClearFilters} /> : null}
              search={false}
              searchText={searchText}
              onSearchChange={setSearchText}
              serverSearch
              resortKey={allowPrivate ? 'private' : 'demo'}
              rowLabel={(row) => row.driverName}
              page={page}
              onPageChange={setPage}
              prefsKey="transaction_logs"
              exportCsv
              exportPrint
              exportFileName={`TransactionLogs_${startDate || 'all'}_to_${endDate || 'all'}`}
              exportTitle={logsTitle}
              exportSubtitle={[rangeText(), formatAddisDate(Date.now())].filter(Boolean).join(' · ')}
              minWidth={1210}
              emptyState={({ query }) =>
                filterTokens.length || query
                  ? {
                      icon: <FilterAltOffOutlinedIcon />,
                      title: t('tx_empty_filtered', { defaultValue: 'No transactions match these filters' }),
                      actionLabel: t('clear_filters', { defaultValue: 'Clear filters' }),
                      onAction: handleClearFilters,
                    }
                  : {
                      icon: <ReceiptLongOutlinedIcon />,
                      title: t('tx_empty', { defaultValue: 'No wallet transactions yet' }),
                    }
              }
            />
          </Box>
        )}

        {/* Tab 2 (admin/finance): Flat-rate plan metrics from the plan's own nodes. */}
        {activeTab === 2 && isAdminOrFinance && <FlatRateMetrics settings={settings} />}

        {/* Tab 3 (admin/finance): Finance & Admin Controls */}
        {activeTab === 3 && isAdminOrFinance && <FinanceAdminControls />}

        {/* Glossary: the last tab (its index depends on the admin/finance tabs). */}
        {activeTab === glossaryIndex && <MetricsGlossary settings={settings} />}
      </Box>
    </Box>
  );
}
